import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { CanvasScheduler } from '@/components/scheduler/CanvasScheduler';
import { useAssignments } from '@/components/assignments/useAssignments';
import { addDays, addHours, startOfToday, format, startOfDay, isWithinInterval, endOfDay, parse, isBefore, isAfter } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/i18n/useTranslation';
import { supabase } from '@/lib/supabase';
import { toUTC, toPlainDateString, parsePlainDateString } from '@/utils/dateTransforms';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AssignmentForm } from '@/components/assignments/AssignmentForm';
import { DateRange } from "react-day-picker";
import { Button } from '@/components/ui/button';
import { Bar, RateOverride } from '@/components/scheduler/schedulerTypes';
import { DateRangePicker } from '@/components/DateRangePicker';
import { DailyHourOverride } from '@/components/assignments/types';
import { Database } from '@/integrations/supabase/types';

let tempIdCounter = -1;
const getNextTempId = () => {
  return tempIdCounter--;
};

const AssignmentDialog = React.memo(({ 
  open, 
  onOpenChange, 
  isNewAssignment, 
  selectedEmployee, 
  selectedAssignment, 
  selectedProject, 
  hourlyRate, 
  dailyHours,
  isBillable,
  overrides,
  projects, 
  assignmentDates, 
  onProjectChange, 
  onHourlyRateChange, 
  onDailyHoursChange, 
  onBillableChange,
  onDatesChange,
  onAddOverride,
  onUpdateOverride,
  onDeleteOverride,
  onCancel, 
  onSave, 
  t 
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isNewAssignment: boolean;
  selectedEmployee: any;
  selectedAssignment: any;
  selectedProject: string;
  hourlyRate: string;
  dailyHours: string;
  isBillable: boolean;
  overrides: DailyHourOverride[];
  projects: any[];
  assignmentDates: DateRange;
  onProjectChange: (value: string) => void;
  onHourlyRateChange: (value: string) => void;
  onDailyHoursChange: (value: string) => void;
  onBillableChange: (value: boolean) => void;
  onDatesChange: (type: 'from' | 'to', value: string) => void;
  onAddOverride: () => void;
  onUpdateOverride: (id: number, field: keyof DailyHourOverride | string, value: string) => Promise<void>;
  onDeleteOverride: (id: number) => Promise<void>;
  onCancel: () => void;
  onSave: () => void;
  t: (key: string) => string;
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] w-[95vw]">
        <DialogHeader>
          <DialogTitle>{isNewAssignment ? t('Create New Assignment') : t('Edit Assignment')}</DialogTitle>
        </DialogHeader>
        
        <AssignmentForm
          selectedEmployee={selectedEmployee}
          selectedAssignment={selectedAssignment}
          selectedProject={selectedProject}
          hourlyRate={hourlyRate}
          dailyHours={dailyHours}
          isBillable={isBillable}
          projects={projects}
          newAssignmentDates={assignmentDates}
          overrides={overrides}
          onProjectChange={onProjectChange}
          onHourlyRateChange={onHourlyRateChange}
          onDailyHoursChange={onDailyHoursChange}
          onBillableChange={onBillableChange}
          onDatesChange={onDatesChange}
          onAddOverride={onAddOverride}
          onUpdateOverride={onUpdateOverride}
          onDeleteOverride={onDeleteOverride}
        />
        
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={onCancel}
          >
            {t('Cancel')}
          </Button>
          <Button 
            onClick={onSave}
          >
            {isNewAssignment ? t('Create Assignment') : t('Update Assignment')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default function Assignments2() {
  const { t } = useTranslation();
  const { employees = [], assignments = [], projects = [], isLoading, refetch } = useAssignments();
  const schedulerRef = useRef<HTMLDivElement>(null);
  const todayButtonRef = useRef<HTMLButtonElement | null>(null);
  const { toast } = useToast();
  
  const [dateRange, setDateRange] = useState(() => {
    const today = startOfToday();
    return {
      startDate: addDays(today, -30),
      endDate: addDays(today, 90)
    };
  });

  const [datePickerRange, setDatePickerRange] = useState<DateRange | undefined>({
    from: dateRange.startDate,
    to: dateRange.endDate,
  });

  useEffect(() => {
    if (datePickerRange?.from && datePickerRange?.to) {
      setDateRange({
        startDate: datePickerRange.from,
        endDate: datePickerRange.to
      });
    }
  }, [datePickerRange]);

  const hasScrolledToToday = useRef(false);
  
  useEffect(() => {
    if (!isLoading && !hasScrolledToToday.current && todayButtonRef.current) {
      const timeoutId = setTimeout(() => {
        todayButtonRef.current?.click();
        hasScrolledToToday.current = true;
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);

  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [isNewAssignment, setIsNewAssignment] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedProject, setSelectedProject] = useState("");
  const [hourlyRate, setHourlyRate] = useState("0");
  const [dailyHours, setDailyHours] = useState("8");
  const [isBillable, setIsBillable] = useState(true);
  const [overrides, setOverrides] = useState<DailyHourOverride[]>([]);
  const [assignmentDates, setAssignmentDates] = useState<DateRange>({
    from: null,
    to: null,
  });
  
  const tracks = useMemo(() => employees.map(employee => ({
    id: String(employee.id),
    name: employee.name,
    category: employee.role
  })), [employees]);
  
  const bars = useMemo(() => {
    return assignments.map(assignment => {
      
      const rateOverrides: RateOverride[] = [];
      
      if (assignment.hour_overrides && Array.isArray(assignment.hour_overrides)) {
        assignment.hour_overrides.forEach(override => {
          if (override.start_date && override.end_date && override.daily_hours !== undefined) {
            const rate = override.daily_hours / 8;
            rateOverrides.push({
              startDate: override.start_date,
              endDate: override.end_date,
              rate: rate,
              isBillable: override.isBillable !== false // Default to true if not explicitly set to false
            });
          }
        });
      }
      
      return {
        id: String(assignment.id),
        trackId: String(assignment.employee_id),
        name: assignment.project.name,
        code: assignment.project.code,
        color: assignment.project.color,
        startDate: assignment.start_date,
        endDate: assignment.end_date,
        assignmentRate: assignment.daily_hours / 8,
        rateOverrides: rateOverrides,
        isBillable: assignment.isBillable !== false // Default to true if not explicitly set to false
      } as Bar;
    });
  }, [assignments]);
  console.log("Bars in assignment.tsx:", bars);

  const handleBarUpdated = useCallback(async (updatedBar) => {
    const assignment = assignments.find((a) => String(a.id) === updatedBar.id);
    
    if (!assignment) {
      console.error("No matching assignment found for bar:", updatedBar);
      toast({
        title: t('Error'),
        description: t('Failed to update assignment: Assignment not found'),
        variant: "destructive",
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('employee_project_assignments')
        .update({
          start_date: updatedBar.startDate,
          end_date: updatedBar.endDate
        })
        .eq('id', assignment.id);
        
      if (error) {
        throw error;
      }
      
      toast({
        title: t('Assignment updated'),
        description: t('The assignment has been successfully updated'),
      });
      
      refetch();
      
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast({
        title: t('Error'),
        description: t('Failed to update assignment: ') + error.message,
        variant: "destructive",
      });
    }
  }, [assignments, toast, t, refetch]);

  const handleGridClicked = useCallback((trackId: string, date: Date) => {
    const employee = employees.find(e => String(e.id) === trackId);
    
    if (!employee) {
      console.error("No employee found for trackId:", trackId);
      return;
    }
    
    setSelectedEmployee(employee);
    setSelectedAssignment(null);
    setIsNewAssignment(true);
    setSelectedProject(projects.length > 0 ? projects[0].id.toString() : "");
    setHourlyRate("0");
    setDailyHours("8");
    setIsBillable(true);
    setOverrides([]);
    
    const endDate = addDays(date, 1);
    
    const startDateTime = addHours(date, 9);
    const endDateTime = addHours(endDate, 17);
    
    setAssignmentDates({
      from: startDateTime,
      to: endDateTime
    });

    // Add isNew: true to new assignments
    setOverrides(prevOverrides => prevOverrides.map(override => ({ 
      ...override, 
      isNew: true,
      isBillable: true // Default to billable
    })));

    setAssignmentDialogOpen(true);
  }, [employees, projects]);

  const handleBarTextClicked = useCallback((bar: Bar) => {
    const assignment = assignments.find(a => String(a.id) === bar.id);
    
    if (!assignment) {
      console.error("No matching assignment found for bar:", bar);
      return;
    }
    
    const employee = employees.find(e => e.id === assignment.employee_id);
    
    if (!employee) {
      console.error("No employee found for assignment:", assignment);
      return;
    }
    
    setSelectedEmployee(employee);
    setSelectedAssignment(assignment);
    setIsNewAssignment(false);
    setSelectedProject(assignment.project.id.toString());
    setHourlyRate(assignment.hourly_rate.toString());
    
    const dailyHoursValue = assignment.daily_hours !== undefined ? 
      assignment.daily_hours.toString() : "8";
    console.log("Daily hours from DB:", assignment.daily_hours);
    setDailyHours(dailyHoursValue);
    
    // Set billable status with default to true
    setIsBillable(assignment.isBillable !== false);
    
    try {
      if (assignment.hour_overrides && Array.isArray(assignment.hour_overrides)) {
        const updatedOverrides = assignment.hour_overrides.map(override => ({
          ...override,
          isBillable: override.isBillable !== false // Default to true if not explicitly set
        }));
        setOverrides(updatedOverrides);
        console.log("Loaded overrides:", updatedOverrides);
      } else {
        setOverrides([]);
      }
    } catch (error) {
      console.error("Error processing hour overrides:", error);
      setOverrides([]);
    }
    
    setAssignmentDates({
      from: assignment.start_date,
      to: assignment.end_date
    });
    
    setAssignmentDialogOpen(true);
  }, [assignments, employees]);

  const handleProjectChange = useCallback((value: string) => {
    setSelectedProject(value);
  }, []);

  const handleHourlyRateChange = useCallback((value: string) => {
    setHourlyRate(value);
  }, []);

  const handleDailyHoursChange = useCallback((value: string) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      setDailyHours("8");
    } else {
      const boundedValue = Math.min(Math.max(numValue, 0), 16);
      setDailyHours(boundedValue.toString());
    }
  }, []);

  const handleBillableChange = useCallback((value: boolean) => {
    setIsBillable(value);
  }, []);

  const handleDatesChange = useCallback((type: 'from' | 'to', value: string) => {
    setAssignmentDates(prev => ({
      ...prev,
      [type]: new Date(value)
    }));
  }, []);

  const handleAddOverride = useCallback(() => {
    setOverrides(prevOverrides => {
      const newId = prevOverrides.length > 0 
        ? Math.max(...prevOverrides.map(override => override.id)) + 1 
        : 1;

      const today = new Date();
      let startDate = new Date();

      if (assignmentDates.from && assignmentDates.to) {
        if (isBefore(today, assignmentDates.from)) {
          startDate = assignmentDates.from;
        } else if (isAfter(today, assignmentDates.to)) {
          startDate = assignmentDates.from; // Default to assignment start if today is after assignment end
        } else {
          startDate = today; // Use today if it's within assignment period
        }
      }

      // Ensure the start date does not overlap with any existing override
      const isOverlapping = (date: Date) => {
        return prevOverrides.some(override => {
          const overrideStartDate = override.start_date;
          const overrideEndDate = override.end_date;

          if (!overrideStartDate || !overrideEndDate) return false;

          return isWithinInterval(date, {
            start: startOfDay(overrideStartDate),
            end: endOfDay(overrideEndDate),
          });
        });
      };

      while (isOverlapping(startDate)) {
        startDate = addDays(startDate, 1); // Increment the start date until no overlap is found
        if (assignmentDates.to && isAfter(startDate, assignmentDates.to)) {
          toast({
            title: t('Cannot add override'),
            description: t('No available start date found within the assignment period.'),
            variant: "destructive",
          });
          return prevOverrides;
        }
      }

      const newOverride: DailyHourOverride = {
        id: newId,
        start_date: startDate,
        end_date: startDate, // Set end_date to the same as start_date
        daily_hours: parseInt(dailyHours) || 8,
        description: '', // Empty by default
        isBillable: true, // Default to billable
        isNew: true // Ensure isNew is set for new overrides
      };

      return [...prevOverrides, newOverride];
    });
  }, [assignmentDates.from, assignmentDates.to, dailyHours, toast, t]);

  const handleUpdateOverride = useCallback(async (id: number, field: keyof DailyHourOverride | string, value: string): Promise<void> => {
    console.log(`Updating override ${id}, field ${field} to value ${value}`);
    
    setOverrides(prev => {
      const newOverrides = prev.map(override => {
        if (override.id === id) {
          if (field === 'isBillable') {
            // Convert string 'true'/'false' to boolean
            return {
              ...override,
              [field]: value === 'true'
            };
          }
          else if (field === 'daily_hours') {
            // Convert string to number for daily_hours
            return {
              ...override,
              [field]: parseInt(value)
            };
          }
          else if (field === 'start_date' || field === 'end_date') {
            // Convert string dates to Date objects
            return {
              ...override,
              [field]: new Date(value)
            };
          }
          return {
            ...override,
            [field]: value, // Update the specific field
          };
        }
        return override;
      });

      console.log('Updated overrides array:', newOverrides);
      return newOverrides;
    });

    return Promise.resolve();
  }, []);

  const handleDeleteOverride = useCallback(async (id: number): Promise<void> => {
    console.log(`Deleting override with ID: ${id}`);
    
    setOverrides(prev => {
      const currentOverrides = [...prev];
      
      console.log("Current overrides before deletion:", currentOverrides);
      
      const getNumericId = (overrideId: any): number | null => {
        if (typeof overrideId === 'number') {
          return overrideId;
        } else if (overrideId && typeof overrideId === 'object') {
          if ('value' in overrideId) {
            const parsedId = parseInt(String(overrideId.value));
            return !isNaN(parsedId) ? parsedId : null;
          } else {
            const parsedId = parseInt(String(overrideId));
            return !isNaN(parsedId) ? parsedId : null;
          }
        }
        return null;
      };
      
      const newOverrides = currentOverrides.filter(override => {
        const overrideId = getNumericId(override.id);
        
        if (overrideId === id) {
          console.log(`Found override to delete. Override ID: ${JSON.stringify(override.id)}, Target ID: ${id}`);
        }
        
        return overrideId !== id;
      });
      
      if (newOverrides.length < currentOverrides.length) {
        console.log(`Successfully removed override with ID ${id}`);
      } else {
        console.log(`No override found with ID ${id}`);
        console.log("All override IDs:", currentOverrides.map(o => JSON.stringify(o.id)));
      }
      
      console.log("Overrides after deletion:", newOverrides);
      return newOverrides;
    });
    
    return Promise.resolve();
  }, []);

  const handleSaveAssignment = useCallback(async () => {
    if (!selectedEmployee || !selectedProject || !assignmentDates.from || !assignmentDates.to) {
      toast({
        title: t('Error'),
        description: t('Please fill in all required fields'),
        variant: "destructive",
      });
      return;
    }

    if (overrides.some(override => !override.description.trim())) {
      toast({
        title: t('Error'),
        description: t('All overrides must have a description.'),
        variant: "destructive",
      });
      return;
    }

    try {
      const hoursValue = parseInt(dailyHours);
      const dailyHoursValue = isNaN(hoursValue) ? 8 : Math.min(Math.max(hoursValue, 0), 16);
      
      const formattedOverrides = overrides.map(override => ({
        id: override.id,
        start_date: toPlainDateString(override.start_date),
        end_date: toPlainDateString(override.end_date),
        daily_hours: override.daily_hours,
        description: override.description,
        isBillable: override.isBillable !== false // Ensure boolean value
      }));
      
      console.log("Saving assignment with daily hours:", dailyHoursValue);
      console.log("With overrides:", formattedOverrides);
      
      const assignmentsToInsert: Database['public']['Tables']['employee_project_assignments']['Insert'][] = [
        {
          employee_id: selectedEmployee.id,
          project_id: parseInt(selectedProject),
          start_date: toPlainDateString(assignmentDates.from),
          end_date: toPlainDateString(assignmentDates.to),
          hourly_rate: parseFloat(hourlyRate),
          daily_hours: dailyHoursValue,
          hour_overrides: formattedOverrides,
          isBillable: isBillable,
          currency: 'USD'
        }
      ];

      if (isNewAssignment) {
        const { error } = await supabase
          .from('employee_project_assignments')
          .insert(assignmentsToInsert);
          
        if (error) {
          throw error;
        }
        
        toast({
          title: t('Assignment created'),
          description: t('The assignment has been successfully created'),
        });
      } else {
        const { error } = await supabase
          .from('employee_project_assignments')
          .update({
            project_id: parseInt(selectedProject),
            start_date: toPlainDateString(assignmentDates.from),
            end_date: toPlainDateString(assignmentDates.to),
            hourly_rate: parseFloat(hourlyRate),
            daily_hours: dailyHoursValue,
            hour_overrides: formattedOverrides,
            isBillable: isBillable
          })
          .eq('id', selectedAssignment.id);
          
        if (error) {
          throw error;
        }
        
        toast({
          title: t('Assignment updated'),
          description: t('The assignment has been successfully updated'),
        });
      }
      
      setAssignmentDialogOpen(false);
      refetch();
      
    } catch (error) {
      console.error("Error saving assignment:", error);
      toast({
        title: t('Error'),
        description: t('Failed to save assignment: ') + error.message,
        variant: "destructive",
      });
    }
  }, [selectedEmployee, selectedProject, assignmentDates, isNewAssignment, hourlyRate, dailyHours, isBillable, overrides, selectedAssignment, toast, t, refetch]);

  const handleDialogClose = useCallback(() => {
    setAssignmentDialogOpen(false);
  }, []);

  const onTodayButtonRef = useCallback((ref: HTMLButtonElement | null) => {
    todayButtonRef.current = ref;
  }, []);

  

  return (
    <div className="p-8 h-screen flex flex-col">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('Assignments')}</h1>
        <DateRangePicker date={datePickerRange} onDateChange={setDatePickerRange} />
      </div>
      
      {isLoading ? (
        <div className="space-y-4 flex-1">
          <Skeleton className="h-full w-full" />
        </div>
      ) : (
        <div ref={schedulerRef} className="flex-1 w-full border rounded-md overflow-hidden" style={{ minHeight: '600px' }}>
          <CanvasScheduler
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            tracks={tracks}
            bars={bars}
            isLoading={isLoading}
            onBarUpdated={handleBarUpdated}
            onGridClicked={handleGridClicked}
            onBarTextClicked={handleBarTextClicked}
            onTodayButtonRef={onTodayButtonRef}
          />
        </div>
      )}

      <AssignmentDialog
        open={assignmentDialogOpen}
        onOpenChange={setAssignmentDialogOpen}
        isNewAssignment={isNewAssignment}
        selectedEmployee={selectedEmployee}
        selectedAssignment={selectedAssignment}
        selectedProject={selectedProject}
        hourlyRate={hourlyRate}
        dailyHours={dailyHours}
        isBillable={isBillable}
        overrides={overrides}
        projects={projects}
        assignmentDates={assignmentDates}
        onProjectChange={handleProjectChange}
        onHourlyRateChange={handleHourlyRateChange}
        onDailyHoursChange={handleDailyHoursChange}
        onBillableChange={handleBillableChange}
        onDatesChange={handleDatesChange}
        onAddOverride={handleAddOverride}
        onUpdateOverride={handleUpdateOverride}
        onDeleteOverride={handleDeleteOverride}
        onCancel={handleDialogClose}
        onSave={handleSaveAssignment}
        t={t}
      />
    </div>
  );
}
